import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { BFF_BASE_URL } from '../../auth/authApi';

export interface User {
  id: string;
  display_name: string;
  email?: string;
  phone_number?: string;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
  last_seen_at?: string;
  conversation_count: number;
  message_count: number;
}

export interface UsersState {
  users: User[];
  /** The user the admin is looking at, shared by every page and kept across reloads. '' means all users. */
  selectedUserId: string;
  loading: boolean;
  error: string | null;
}

const SELECTED_USER_KEY = 'victoria-admin:selected-user';

function loadSelectedUserId(): string {
  try {
    return localStorage.getItem(SELECTED_USER_KEY) ?? '';
  } catch {
    return '';
  }
}

function saveSelectedUserId(id: string) {
  try {
    if (id) localStorage.setItem(SELECTED_USER_KEY, id);
    else localStorage.removeItem(SELECTED_USER_KEY);
  } catch {
    // Storage can be blocked; the selection then lasts until reload.
  }
}

export const usersInitialState: UsersState = {
  users: [],
  selectedUserId: loadSelectedUserId(),
  loading: false,
  error: null,
};

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BFF_BASE_URL}/admin/users`);
      console.log('Users API response:', response.data);
      console.log('First user sample:', response.data.users?.[0]);
      if (response.data.users?.[0]) {
        console.log('User fields:', Object.keys(response.data.users[0]));
      }
      return response.data.users;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.error || error.message);
      }
      return rejectWithValue('Failed to fetch users');
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState: usersInitialState,
  reducers: {
    setUsers: (state, action: PayloadAction<User[]>) => {
      state.users = action.payload;
      state.loading = false;
      state.error = null;
    },
    addUser: (state, action: PayloadAction<User>) => {
      state.users.push(action.payload);
    },
    updateUser: (state, action: PayloadAction<User>) => {
      const index = state.users.findIndex(u => u.id === action.payload.id);
      if (index !== -1) {
        state.users[index] = action.payload;
      }
    },
    deleteUser: (state, action: PayloadAction<string>) => {
      state.users = state.users.filter(u => u.id !== action.payload);
    },
    setSelectedUserId: (state, action: PayloadAction<string>) => {
      state.selectedUserId = action.payload;
      saveSelectedUserId(action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.users = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setUsers,
  addUser,
  updateUser,
  deleteUser,
  setSelectedUserId,
  setLoading,
  setError,
} = usersSlice.actions;

export default usersSlice;
